import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  FileDownload as CsvIcon,
  GridView as ColumnIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Layout from '../../components/layout/Layout';
import activityLogService from '../../services/activityLogService';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const ActivityLogPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [columnVisibility, setColumnVisibility] = useState({
    date: true,
    subjectType: true,
    action: true,
    by: true,
    note: true,
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await activityLogService.getActivityLogs({
        page,
        size: rowsPerPage,
        search: searchDebounced || undefined,
      });
      setLogs(Array.isArray(data.content) ? data.content : []);
      setTotalElements(data.totalElements != null ? data.totalElements : 0);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      enqueueSnackbar('Failed to load activity logs', { variant: 'error' });
      setLogs([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchDebounced, enqueueSnackbar]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateVal) => {
    if (dateVal == null) return '';
    try {
      let d;
      if (Array.isArray(dateVal)) {
        // Java LocalDateTime can be serialized as [year, month, day, hour, minute, second, nano]
        const [y, mo, day, h = 0, min = 0, sec = 0] = dateVal;
        if (y == null || mo == null || day == null) return '';
        d = new Date(y, (mo || 1) - 1, day || 1, h, min, sec);
      } else if (typeof dateVal === 'string') {
        d = new Date(dateVal);
      } else if (typeof dateVal === 'number') {
        d = new Date(dateVal);
      } else {
        return '';
      }
      return isNaN(d.getTime()) ? '' : format(d, 'MM/dd/yyyy HH:mm');
    } catch {
      return '';
    }
  };

  const exportToCsv = () => {
    const headers = ['Date', 'Subject Type', 'Action', 'By', 'Note'];
    const rows = logs.map((row) => [
      formatDate(row.date),
      row.subjectType || '',
      row.action || '',
      row.performedByName || row.performedByEmail || '',
      (row.note || '').replace(/\n/g, ' '),
    ]);
    const csvContent = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `activity-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    enqueueSnackbar('CSV exported', { variant: 'success' });
  };

  const handlePrint = () => {
    window.print();
  };

  const getActionChipColor = (action) => {
    switch (action) {
      case 'LOGIN':
        return 'success';
      case 'LOGOUT':
        return 'default';
      case 'ADDED':
        return 'primary';
      case 'EDITED':
        return 'warning';
      case 'CONVERTED':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSubjectTypeLabel = (subjectType) => {
    const map = { USER: 'User', SELL: 'Sell', PURCHASE: 'Purchase', CONTACT: 'Contact' };
    return map[subjectType] || subjectType || '';
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/settings" underline="hover" color="inherit">
            Settings
          </Link>
          <Typography color="text.primary">Activity Log</Typography>
        </Breadcrumbs>

        <Typography variant="h5" sx={{ mb: 2 }} color="text.primary">
          Activity Log
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          View login/logout times, sales, bill edits, and purchase activities for your company.
        </Typography>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Show entries</InputLabel>
              <Select
                value={rowsPerPage}
                label="Show entries"
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
              >
                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Tooltip title="Export to CSV">
                <IconButton onClick={exportToCsv} size="small" color="primary">
                  <CsvIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print">
                <IconButton onClick={handlePrint} size="small">
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Column visibility">
                <IconButton
                  size="small"
                  onClick={() =>
                    setColumnVisibility((prev) => ({
                      ...prev,
                      note: !prev.note,
                    }))
                  }
                >
                  <ColumnIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ ml: 'auto', minWidth: 200 }}
            />
          </Box>

          <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columnVisibility.date && (
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  )}
                  {columnVisibility.subjectType && (
                    <TableCell sx={{ fontWeight: 600 }}>Subject Type</TableCell>
                  )}
                  {columnVisibility.action && (
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  )}
                  {columnVisibility.by && (
                    <TableCell sx={{ fontWeight: 600 }}>By</TableCell>
                  )}
                  {columnVisibility.note && (
                    <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && logs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      No activity logs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((row) => (
                    <TableRow key={row.id} hover>
                      {columnVisibility.date && (
                        <TableCell>{formatDate(row.date)}</TableCell>
                      )}
                      {columnVisibility.subjectType && (
                        <TableCell>{getSubjectTypeLabel(row.subjectType)}</TableCell>
                      )}
                      {columnVisibility.action && (
                        <TableCell>
                          <Chip
                            label={row.action}
                            size="small"
                            color={getActionChipColor(row.action)}
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      {columnVisibility.by && (
                        <TableCell>
                          {row.performedByName || row.performedByEmail || '-'}
                          {row.userRole && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              ({row.userRole})
                            </Typography>
                          )}
                          {row.locationName && (
                            <Typography component="span" variant="caption" display="block" color="text.secondary">
                              {row.locationName}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {columnVisibility.note && (
                        <TableCell sx={{ whiteSpace: 'pre-wrap', maxWidth: 360 }}>
                          {row.note || ''}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Container>
    </Layout>
  );
};

export default ActivityLogPage;
